import { Box, Typography, Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import SentryErrorModal from '../SentryErrorModal/SentryErrorModal';

/**
 * CustomErrorBoundary handles React component errors and crashes.
 * It does NOT handle HTTP 500 errors from the backend - those are handled
 * separately in the axios interceptor and app.tsx.
 *
 * This boundary specifically catches:
 * - React component rendering errors
 * - JavaScript runtime errors in React components
 * - Unhandled promise rejections in React components
 */

interface CustomErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorState {
  hasError: boolean;
  error?: Error;
  eventId?: string;
}

// Fallback component for when React errors occur
const ErrorFallback: React.FC<{ error?: Error; eventId?: string }> = ({
  error,
  eventId,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 3,
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
    }}
  >
    <Typography variant="h4" color="error" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      We&apos;ve encountered an unexpected error. Our team has been notified.
    </Typography>
    {eventId && (
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        Error ID: {eventId}
      </Typography>
    )}
    <Button
      variant="contained"
      color="primary"
      onClick={() => window.location.reload()}
      sx={{ mt: 2 }}
    >
      Reload Page
    </Button>
  </Box>
);

export const CustomErrorBoundary: React.FC<CustomErrorBoundaryProps> = ({
  children,
}) => {
  const [errorState, setErrorState] = useState<ErrorState>({ hasError: false });
  const [showModal, setShowModal] = useState(false);

  const handleError = (error: Error, eventId: string) => {
    console.log('CustomErrorBoundary caught React error/crash:', error);

    setErrorState({
      hasError: true,
      error,
      eventId,
    });

    // Show the feedback modal for React errors
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Don't reset error state here - let the user reload the page
    // This ensures the error boundary stays in error state until page reload
  };

  // If there's a React error, show the fallback UI
  if (errorState.hasError) {
    return (
      <>
        <ErrorFallback error={errorState.error} eventId={errorState.eventId} />
        <SentryErrorModal
          open={showModal}
          onClose={handleModalClose}
          error={errorState.error}
          eventId={errorState.eventId}
        />
      </>
    );
  }

  // Normal state - wrap children with Sentry ErrorBoundary
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId }) => (
        <ErrorFallback error={error} eventId={eventId} />
      )}
      onError={handleError}
      // Only capture React errors, not HTTP errors
      beforeCapture={(scope) => {
        // Add context to distinguish React errors from HTTP errors
        scope.setTag('error_type', 'react_component_error');
        scope.setContext('error_boundary', {
          component: 'CustomErrorBoundary',
          error_source: 'react_component',
        });
      }}
    >
      {children}
      <SentryErrorModal
        open={showModal}
        onClose={handleModalClose}
        error={errorState.error}
        eventId={errorState.eventId}
      />
    </Sentry.ErrorBoundary>
  );
};

export default CustomErrorBoundary;
